import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAdminAppearance } from '../../contexts/AdminAppearanceContext';
import ContinueActiveItemModal from '../auth/ContinueActiveItemModal';
import CloseActiveItemModal from '../auth/CloseActiveItemModal';
import ProductionTable from './ProductionTable';
import { translateProblemTypeDescription, translateProblemTypeName, isNoDyeProblem } from '../../utils/problemTypeDisplay';
import { getProductionQuantitySaveErrorAlert } from '../../utils/productionQuantityErrors';
import QRCodeModal from '../warehouse/QRCodeModal';
import PrintedSpoolsList from '../warehouse/PrintedSpoolsList';
import AutoRefreshCountdown from '../common/AutoRefreshCountdown';
import { formatKg, parseQty, parseWeightKg } from '../../utils/productionKg';
import { canMarkToRewind, isQuantityLockedByRewind } from '../../utils/rewindEntries';

// QR label window for the spool sent to the rewinding station (label 76 x 54 mm).
// Setting this to `false` hides it again; the `qrItem` logic stays untouched either way.
// Note: with the window hidden there is nowhere to enter the measured spool weight (`actual_weight`).
const SHOW_QR_MODAL = true;

interface ProductionLine {
  id: number;
  name: string;
  spool_type?: 'small' | 'big';
}

interface ProductionItem {
  id: number;
  linia: string | null;
  tydzien: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  barwnik_dostarczony?: boolean;
  /** 'available' | 'missing' | null — dye availability marked by a person. */
  barwnik_status?: 'available' | 'missing' | null;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
  ean: string | null;
  zmiana_1: string | null;
  zmiana_2: string | null;
  /** Counted from weighed big-spool labels: zmiana_1 / zmiana_2 hold the whole pieces of the label weights. */
  weighed_spools?: boolean;
  uwagi: string | null;
  kolejnosc?: number | null;
  pivot?: {
    status: 'pending' | 'in_progress' | 'completed' | 'problem_bb' | 'problem_awaria' | 'niepelna_produkcja';
    notes: string | null;
    notatka?: string | null;
    wykonano_ilosc?: number | null;
    user_id?: number | null;
    started_at?: string | null;
  };
  notatki?: Array<{ id: number; tresc: string; created_at: string; user?: { id: number; name: string } | null }>;
  rewind_entries?: Array<{ id: number; przewinieta: boolean; marked_to_rewind?: boolean }>;
}

interface Assignment {
  id: number;
  production_line_id: number;
  productionLine: { id: number; name: string };
  week: string;
  status: string;
  items: ProductionItem[];
}

interface WeekMeta {
  production_line_id: number;
  week: string;
  is_closed: boolean;
  is_empty: boolean;
  /** Planned kilograms of the line+week — this is the 100% the progress bar measures against. */
  planned_kg: number;
  /** Produced kilograms counted towards the plan (capped at the plan per item). */
  produced_kg: number;
  completion_percent: number;
}

interface MyAssignmentsResponse {
  assignments: Assignment[];
  production_lines: ProductionLine[];
  available_weeks: WeekMeta[];
}

/** Item statuses that mean production started and was not closed. */
const UNFINISHED_STATUSES: string[] = ['in_progress', 'problem_bb', 'problem_awaria'];

const MyProduction: React.FC = () => {
  const { selectedLine, selectedShift, user } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en' ? 'en-GB' : language === 'ru' ? 'uk-UA' : 'pl-PL';
  const { columnWidthLeft, columnWidthRight } = useAdminAppearance();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [productionLines, setProductionLines] = useState<ProductionLine[]>([]);

  // Initialize selectedLineId as null, will be loaded from localStorage after user is available
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  // Najnowszy wybór linii dla fetchAssignments (wołanego też z domknięć sprzed zmiany stanu).
  const selectedLineIdRef = useRef<number | null>(null);
  selectedLineIdRef.current = selectedLineId;
  // Linia, o którą poszło ostatnie pobranie /my-assignments.
  const requestedLineIdRef = useRef<number | null>(null);

  // Load selectedLineId from localStorage when user is available
  useEffect(() => {
    if (user?.id) {
      const storageKey = `selectedLineId_${user.id}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const lineId = parseInt(stored, 10);
        if (!isNaN(lineId)) {
          setSelectedLineId(lineId);
        }
      }
    }
  }, [user?.id]);

  // Przejście z listy linii ("Przejdź"): ?line=ID wymusza wybór tej linii.
  // Reaguje za każdym razem, gdy zmienia się param (niezależnie od (re)montowania).
  useEffect(() => {
    const lineParam = searchParams.get('line');
    if (!lineParam) return;
    const lineId = parseInt(lineParam, 10);
    if (!isNaN(lineId)) {
      setSelectedLineId(lineId);
      if (user?.id) {
        localStorage.setItem(`selectedLineId_${user.id}`, String(lineId));
      }
    }
    // wyczyść param, żeby nie blokował późniejszej zmiany linii z listy rozwijanej
    searchParams.delete('line');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, user?.id, setSearchParams]);
  const [currentItems, setCurrentItems] = useState<ProductionItem[]>([]);
  const [editingNotes, setEditingNotes] = useState<{ itemId: number; assignmentId: number } | null>(null);
  const [selectedProblemTypeId, setSelectedProblemTypeId] = useState<number | null>(null);
  const [notatkiList, setNotatkiList] = useState<Array<{ id: number; tresc: string; created_at: string; user?: { id: number; name: string } | null }>>([]);
  const [newNotatka, setNewNotatka] = useState('');
  const [savingNotatka, setSavingNotatka] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [problemTypes, setProblemTypes] = useState<Array<{ id: number; name: string; description: string | null; is_active?: boolean }>>([]);
  const [notesHistory, setNotesHistory] = useState<any[]>([]);
  const [loadingNotesHistory, setLoadingNotesHistory] = useState(false);
  const [editingQuantity, setEditingQuantity] = useState<{ itemId: number; field: 'zmiana_1' | 'zmiana_2' } | null>(null);
  const [quantityValue, setQuantityValue] = useState('');
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [qrItem, setQrItem] = useState<any | null>(null);
  // Weighed big-spool label window (big-spool lines) — opened from the Z1/Z2 cells.
  const [spoolTarget, setSpoolTarget] = useState<{ itemId: number; assignmentId: number; field: 'zmiana_1' | 'zmiana_2' } | null>(null);
  const [barwnikItem, setBarwnikItem] = useState<(ProductionItem & { assignmentId?: number }) | null>(null);
  const [savingBarwnik, setSavingBarwnik] = useState(false);
  const [quantityHistory, setQuantityHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showNegativeConfirm, setShowNegativeConfirm] = useState(false);
  const [negativeHoldProgress, setNegativeHoldProgress] = useState(0);
  const negativeHoldTimerRef = useRef<NodeJS.Timeout | null>(null);
  const negativeHoldStartRef = useRef<number | null>(null);
  const [showCompleteOnLimitModal, setShowCompleteOnLimitModal] = useState(false);
  const [itemReachedLimit, setItemReachedLimit] = useState<{ item: ProductionItem; assignmentId: number; field: 'zmiana_1' | 'zmiana_2' } | null>(null);
  const [showStartProductionModal, setShowStartProductionModal] = useState(false);
  const [pendingItem, setPendingItem] = useState<{ item: ProductionItem & { assignmentId?: number }; assignmentId: number } | null>(null);
  // Items on the line produced in full but not closed — before another item starts, the operator is
  // asked about each of them in turn whether its production is finished.
  const [unfinishedItems, setUnfinishedItems] = useState<Array<{ item: ProductionItem; assignmentId: number }>>([]);
  const [closingUnfinished, setClosingUnfinished] = useState(false);
  const [showCompleteProductionModal, setShowCompleteProductionModal] = useState(false);
  // Duża linia: ręczne oznaczenie pozycji „Do przewinięcia" klikiem w wiersz. Wydruk etykiety
  // ważonej szpuli wysyła pozycję na Stację przewijania sam, bez tego okna.
  // source: 'quantity' — po zapisie podglądowej ilości, 'row' — klik w zrealizowany wiersz.
  const [rewindPrompt, setRewindPrompt] = useState<{ item: ProductionItem; assignmentId: number } | null>(null);
  const [showProblemOptionsModal, setShowProblemOptionsModal] = useState(false);
  const [showIncompleteProductionModal, setShowIncompleteProductionModal] = useState(false);
  const [incompleteQuantity, setIncompleteQuantity] = useState('');
  const [activeItem, setActiveItem] = useState<{ item: ProductionItem & { assignmentId?: number }; assignmentId: number } | null>(null);
  const [showCancelProductionModal, setShowCancelProductionModal] = useState(false);
  const [itemToCancel, setItemToCancel] = useState<{ item: ProductionItem & { assignmentId?: number }; assignmentId: number } | null>(null);
  const [otherUserActiveItem, setOtherUserActiveItem] = useState<any>(null);
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  // Pełna lista tygodni per linia z backendu (z flagami is_closed/is_empty) — lekki agregat.
  const [allWeeks, setAllWeeks] = useState<WeekMeta[]>([]);
  // Tygodnie widoczne na pasku dla wybranej linii (wszystkie: otwarte, ukończone=zielone, puste=szare).
  const [availableWeeks, setAvailableWeeks] = useState<WeekMeta[]>([]);
  // Tygodnie doładowywane na żądanie (klik w ukończony/zielony tydzień) — spinner na kafelku.
  const [loadingWeek, setLoadingWeek] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDowntime, setActiveDowntime] = useState<{ id: number; type: string; started_at: string } | null>(null);
  const [downtimeElapsed, setDowntimeElapsed] = useState('00:00');

  useEffect(() => {
    fetchAssignments();
    fetchProblemTypes();
  }, []);

  // Pozycje są pobierane per linia — po zmianie linii (lista, ?line, localStorage, linia
  // z logowania) dociągamy jej dane, o ile ostatnie pobranie nie dotyczyło już tej linii.
  useEffect(() => {
    if (selectedLineId && selectedLineId !== requestedLineIdRef.current) {
      fetchAssignments(true, selectedLineId);
    }
  }, [selectedLineId]);

  // Pasek tygodni pochodzi z lekkiej listy `allWeeks` z backendu (per linia, z flagami
  // is_closed/is_empty) — pokazujemy WSZYSTKIE tygodnie wybranej linii, w tym ukończone (zielone)
  // i puste (szare). Domyślnie zaznaczamy ostatni OTWARTY tydzień, żeby wejść na aktywną pracę.
  useEffect(() => {
    const lineWeeks = (selectedLineId
      ? allWeeks.filter(w => w.production_line_id === selectedLineId)
      : allWeeks
    ).slice().sort((a, b) => a.week.localeCompare(b.week));
    setAvailableWeeks(lineWeeks);

    const openWeeks = lineWeeks.filter(w => !w.is_closed && !w.is_empty);
    const stillValid = selectedWeek !== null && lineWeeks.some(w => w.week === selectedWeek);
    if (!stillValid) {
      setSelectedWeek(openWeeks.length > 0 ? openWeeks[openWeeks.length - 1].week : null);
    }
  }, [allWeeks, selectedLineId]);

  // Wybór tygodnia. Ukończone/puste tygodnie nie są w głównym pobraniu (fix OOM), więc gdy
  // klikniesz taki tydzień, doładowujemy jego pozycje na żądanie (jedna linia + tydzień = lekkie)
  // i wmerge'owujemy do stanu — reszta widoku (currentItems) sama się przeliczy.
  const selectWeek = async (week: string | null) => {
    setSelectedWeek(week);
    if (!week || !selectedLineId) return;
    const alreadyLoaded = assignments.some(
      a => a.production_line_id === selectedLineId && a.week === week
    );
    if (alreadyLoaded) return;
    setLoadingWeek(week);
    try {
      const res = await api.get<{ assignments: Assignment[] }>('/my-assignments/week', {
        params: { production_line_id: selectedLineId, week },
      });
      const fetched = res.data.assignments || [];
      setAssignments(prev => {
        const ids = new Set(prev.map(a => a.id));
        return [...prev, ...fetched.filter(a => !ids.has(a.id))];
      });
    } catch (error) {
      console.error('Error loading week items:', error);
    } finally {
      setLoadingWeek(null);
    }
  };

  // Save selectedLineId to localStorage whenever it changes
  useEffect(() => {
    if (selectedLineId !== null && user?.id) {
      const storageKey = `selectedLineId_${user.id}`;
      localStorage.setItem(storageKey, selectedLineId.toString());
    } else if (selectedLineId !== null) {
      // Fallback if user is not loaded yet
      localStorage.setItem('selectedLineId', selectedLineId.toString());
    }
  }, [selectedLineId, user?.id]);

  const fetchProblemTypes = async () => {
    try {
      const response = await api.get('/problem-types');
      const types = response.data || [];
      setProblemTypes(types);
      if (types.length === 0) {
        console.warn('Brak dostępnych typów problemów. Upewnij się, że administrator dodał aktywne typy problemów.');
      }
    } catch (error: any) {
      console.error('Error fetching problem types:', error);
      alert(`${t('common.error')}: ${t('myProduction.errorFetchingProblemTypes')}: ${error.response?.data?.message || t('myProduction.unknownError')}`);
    }
  };

  const fetchNotesHistory = async (assignmentId: number, itemId: number) => {
    setLoadingNotesHistory(true);
    try {
      const response = await api.get(`/production-assignments/${assignmentId}/items/${itemId}/notes-history`);
      setNotesHistory(response.data || []);
    } catch (error: any) {
      console.error('Error fetching notes history:', error);
      setNotesHistory([]);
    } finally {
      setLoadingNotesHistory(false);
    }
  };

  useEffect(() => {
    if (selectedLineId) {
      // Get all items from all assignments for selected line and selected week
      const lineAssignments = assignments.filter((a) => {
        const matchesLine = a.production_line_id === selectedLineId;
        const matchesWeek = !selectedWeek || a.week === selectedWeek;
        return matchesLine && matchesWeek;
      });
      
      // Combine all items from all assignments for this line
      // Keep track of assignment ID for each item
      const allItems: Array<ProductionItem & { assignmentId: number }> = [];
      lineAssignments.forEach((assignment) => {
        assignment.items.forEach((item) => {
          allItems.push({
            ...item,
            pivot: item.pivot || { status: 'pending', notes: null },
            assignmentId: assignment.id,
          } as ProductionItem & { assignmentId: number });
        });
      });
      
      // Group by item id and keep the one with the most recent status
      const itemsMap = new Map<number, ProductionItem & { assignmentId: number }>();
      allItems.forEach((item) => {
        const existing = itemsMap.get(item.id);
        if (!existing || (existing.pivot?.status === 'pending' && item.pivot?.status !== 'pending')) {
          itemsMap.set(item.id, item);
        }
      });
      
      let items = Array.from(itemsMap.values()).sort((a, b) => {
        const ka = a.kolejnosc ?? null;
        const kb = b.kolejnosc ?? null;
        if (ka === null && kb === null) return a.id - b.id;
        if (ka === null) return 1;
        if (kb === null) return -1;
        return ka - kb;
      });
      const query = searchQuery.toLowerCase().trim();
      if (query) {
        items = items.filter((item) =>
          (item.indeks_kat || '').toLowerCase().includes(query) ||
          (item.nazwa_oryg || '').toLowerCase().includes(query)
        );
      }
      setCurrentItems(items);
    } else {
      setCurrentItems([]);
    }
  }, [selectedLineId, selectedWeek, assignments, searchQuery]);

  // Linia, której pozycje pobieramy: aktualnie wybrana, a przy pierwszym ładowaniu (zanim
  // efekty ustawią stan) ta sama kolejność co niżej — ?line, localStorage, linia z logowania.
  const preferredLineId = (): number | null => {
    if (selectedLineIdRef.current) return selectedLineIdRef.current;
    const fromParam = parseInt(searchParams.get('line') || '', 10);
    if (!isNaN(fromParam)) return fromParam;
    const stored = parseInt((user?.id && localStorage.getItem(`selectedLineId_${user.id}`)) || '', 10);
    if (!isNaN(stored)) return stored;
    return selectedLine?.id ?? null;
  };

  const fetchAssignments = async (silent = false, lineId: number | null = preferredLineId()): Promise<void> => {
    // Zapamiętane już przy wysłaniu, żeby efekt zmiany linii nie dublował tego samego żądania.
    requestedLineIdRef.current = lineId;
    try {
      // Widok pokazuje zawsze jedną linię, więc pobieramy tylko jej pozycje (pełna lista
      // wszystkich linii użytkownika to były megabajty przy każdym kliknięciu).
      const response = await api.get<MyAssignmentsResponse>('/my-assignments', {
        params: lineId ? { production_line_id: lineId } : undefined,
      });
      const fetched = response.data.assignments || [];
      // Podmieniamy tylko pozycje pobranej linii — dane innych linii zostają do czasu ich odświeżenia.
      setAssignments(prev => (lineId
        ? [...prev.filter(a => a.production_line_id !== lineId), ...fetched]
        : fetched));
      setProductionLines(response.data.production_lines || []);
      setAllWeeks(response.data.available_weeks || []);
      // weeks are recalculated in useEffect below when selectedLineId changes
      
      // Priority: aktualnie wybrana linia (param ?line / localStorage) > linia z logowania > pierwsza dostępna.
      // Używamy funkcyjnego setState, by czytać NAJNOWSZY stan (uniknięcie nadpisania
      // wyboru ustawionego przez efekt ?line, który działa równolegle z tym fetchem).
      const availableLineIds = response.data.production_lines?.map(l => l.id) || [];
      setSelectedLineId((prev) => {
        if (prev && availableLineIds.includes(prev)) {
          return prev; // już wybrana i dostępna — zostaw
        }
        if (selectedLine && availableLineIds.includes(selectedLine.id)) {
          return selectedLine.id;
        }
        if (availableLineIds.length > 0) {
          return availableLineIds[0];
        }
        return prev;
      });
      
      // Check for active items belonging to other users.
      // Skipped on the background auto-refresh — it must not throw a modal over the
      // operator's screen every 10 minutes.
      if (!silent) {
        await checkActiveItemsFromOtherUsers();
      }
    } catch (error: any) {
      // Linia z localStorage / ?line, do której użytkownik nie ma już dostępu — pobieramy bez
      // zawężenia, a wybór linii poniżej przestawi się na dostępną (jak przed zmianą).
      if (lineId && error.response?.status === 403) {
        return fetchAssignments(silent, null);
      }
      console.error('Error fetching assignments:', error);
      // Background auto-refresh must not spam alerts or wipe the table on a network blip
      if (silent) return;
      alert(`${t('common.error')}: ${t('myProduction.errorFetchingData')}: ${error.response?.data?.message || t('myProduction.unknownError')}`);
      setAssignments([]);
      setProductionLines([]);
    }
  };

  const checkActiveItemsFromOtherUsers = async () => {
    try {
      const response = await api.get('/check-active-items');
      const activeItems = response.data.active_items || [];
      
      if (activeItems.length > 0) {
        // Take the first active item (there should be only one active at a time per line)
        setOtherUserActiveItem(activeItems[0]);
        setShowContinueModal(true);
      }
    } catch (error) {
      console.error('Error checking active items:', error);
    }
  };

  const handleContinueOtherUserActiveItem = async () => {
    if (!otherUserActiveItem) return;
    
    try {
      await api.post('/continue-active-item', {
        assignment_id: otherUserActiveItem.assignment_id,
        item_id: otherUserActiveItem.item_id,
      });
      
      setOtherUserActiveItem(null);
      setShowContinueModal(false);
      // Refresh assignments to show updated status
      fetchAssignments();
    } catch (err: any) {
      alert(`${t('common.error')}: ${err.response?.data?.message || t('activeItem.couldNotContinue')}`);
    }
  };

  const handleCancelContinueOtherUser = () => {
    setShowContinueModal(false);
    setShowCloseModal(true);
  };

  const handleCloseOtherUserActiveItem = async (
    reason: 'niepelna_produkcja' | 'problem_bb' | 'problem_awaria' | 'pending',
    wykonanoIlosc?: number
  ) => {
    if (!otherUserActiveItem) return;
    if (!selectedShift && reason === 'niepelna_produkcja') {
      alert(t('shift.mustSelectShift'));
      return;
    }
    
    try {
      const payload: any = {
        assignment_id: otherUserActiveItem.assignment_id,
        item_id: otherUserActiveItem.item_id,
        reason,
      };
      
      if (reason === 'niepelna_produkcja' && wykonanoIlosc !== undefined) {
        payload.wykonano_ilosc = wykonanoIlosc;
        if (selectedShift) {
          payload.shift = selectedShift;
        }
      }
      
      await api.post('/close-active-item', payload);
      
      setOtherUserActiveItem(null);
      setShowCloseModal(false);
      // Refresh assignments to show updated status
      fetchAssignments();
    } catch (err: any) {
      alert(`${t('common.error')}: ${err.response?.data?.message || t('activeItem.couldNotClose')}`);
    }
  };

  const handleCancelCloseOtherUser = () => {
    // User cancelled closing - go back to continue modal
    setShowCloseModal(false);
    setShowContinueModal(true);
  };

  // Ustaw linię z logowania tylko, gdy NIC nie jest jeszcze wybrane.
  // Funkcyjny setState czyta najnowszy stan — nie nadpisuje wyboru z ?line / localStorage / dropdowna.
  useEffect(() => {
    if (selectedLine) {
      setSelectedLineId((prev) => prev ?? selectedLine.id);
    }
  }, [selectedLine]);

  // Fetch active downtime when line changes
  useEffect(() => {
    if (selectedLineId) {
      api.get('/production-downtimes/active', { params: { production_line_id: selectedLineId } })
        .then((res) => {
          const d = res.data;
          setActiveDowntime(d && d.id && d.started_at ? d : null);
        })
        .catch(() => setActiveDowntime(null));
    } else {
      setActiveDowntime(null);
    }
  }, [selectedLineId]);

  // Timer for active downtime
  useEffect(() => {
    if (!activeDowntime) {
      setDowntimeElapsed('00:00');
      return;
    }
    const update = () => {
      const start = new Date(activeDowntime.started_at).getTime();
      const diff = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const mins = Math.floor(diff / 60).toString().padStart(2, '0');
      const secs = (diff % 60).toString().padStart(2, '0');
      setDowntimeElapsed(`${mins}:${secs}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activeDowntime]);

  const handleStartDowntime = async (type: 'czyszczenie' | 'awaria') => {
    if (!selectedLineId) return;
    try {
      const res = await api.post('/production-downtimes/start', {
        production_line_id: selectedLineId,
        type,
      });
      setActiveDowntime(res.data);
    } catch (error: any) {
      alert(error.response?.data?.message || t('downtime.startFailed'));
    }
  };

  const handleStopDowntime = async () => {
    if (!selectedLineId) return;
    try {
      await api.post('/production-downtimes/stop', {
        production_line_id: selectedLineId,
      });
      setActiveDowntime(null);
    } catch (error: any) {
      alert(error.response?.data?.message || t('downtime.stopFailed'));
    }
  };

  // Typ szpuli aktualnie wybranej linii. Na liniach dużych szpul klik w Z1/Z2 otwiera
  // ważenie szpuli: wydruk etykiety dolicza wagę (kg) do klikniętej zmiany i wysyła
  // pozycję na Stację przewijania.
  const currentLine = productionLines.find((l) => l.id === selectedLineId);
  const isBigSpoolLine = currentLine?.spool_type === 'big';

  // Realizacja listy w kilogramach dla wybranej linii: wybrany tydzień, albo suma wszystkich
  // tygodni linii, gdy wybrano „Wszystkie". Liczby pochodzą z backendu (available_weeks),
  // więc obejmują też tygodnie, których pozycji nie ładujemy do tabeli.
  const completion = useMemo(() => {
    const weeks = selectedWeek === null
      ? availableWeeks
      : availableWeeks.filter(w => w.week === selectedWeek);

    const plannedKg = weeks.reduce((sum, w) => sum + (w.planned_kg ?? 0), 0);
    const producedKg = weeks.reduce((sum, w) => sum + (w.produced_kg ?? 0), 0);

    return {
      plannedKg,
      producedKg,
      percent: plannedKg > 0 ? Math.min((producedKg / plannedKg) * 100, 100) : 0,
    };
  }, [availableWeeks, selectedWeek]);

  const getItemStatus = (item: ProductionItem): 'pending' | 'in_progress' | 'completed' | 'problem_bb' | 'problem_awaria' | 'niepelna_produkcja' => {
    return item.pivot?.status || 'pending';
  };

  const getRowColor = (item: ProductionItem): string => {
    const hasNotes = !!(item.pivot?.notes || item.uwagi);
    const doProdukcji = item.do_produkcji ? parseInt(item.do_produkcji) : 0;
    const wyprodukowano = parseQty(item.zmiana_1) + parseQty(item.zmiana_2);

    const status = getItemStatus(item);

    // "Brak barwnika" podświetla tylko komórkę Barwnik (patrz ProductionTable), wiersz zostaje biały
    const noDye = isNoDyeProblem(item.pivot?.status, item.pivot?.notes || item.uwagi);

    // Pozycja w CAŁOŚCI przewinięta jest gotowa → zielona (i nie miga), niezależnie od uwagi
    // czy niepełnej ilości. Musi być PRZED czerwonym z uwagi, inaczej przewinięta pozycja
    // z uwagą dalej migałaby.
    const rewindAll = Array.isArray(item.rewind_entries) ? item.rewind_entries : [];
    if (rewindAll.length > 0 && !rewindAll.some((e) => !e.przewinieta)) return '#d4edda';

    // Red if has notes and not fully produced (z wyjątkiem "brak barwnika" i pozycji zamkniętych/zakończonych)
    if (hasNotes && !noDye && wyprodukowano < doProdukcji && status !== 'completed') return '#f8d7da';

    // Przewijanie ma pierwszeństwo nad "w pełni wyprodukowane": pozycja oznaczona jako
    // "do przewinięcia" (niebieski) pozostaje niebieska nawet gdy nawijanie jest już
    // ukończone – aż zostanie w całości przewinięta.
    //   niebieski  = gotowe/w trakcie przewijania (jest wpis oczekujący na przewinięcie)
    //   zielony    = wszystko przewinięte
    const rewindEntries = Array.isArray(item.rewind_entries) ? item.rewind_entries : [];
    // Niebieski dopiero po ręcznym oznaczeniu przyciskiem „Do przewijania" (marked_to_rewind).
    // Szpula wysłana na stację automatycznie po przekroczeniu progu wagi czeka tam, ale wiersz
    // na linii nie zmienia koloru, dopóki operator jej nie oznaczy.
    if (rewindEntries.some((e) => !e.przewinieta && e.marked_to_rewind)) return '#cfe2ff'; // niebieski
    if (rewindEntries.some((e) => e.przewinieta)) return '#d4edda'; // zielony (przewinięte)

    // Na liniach dużych szpul zakończenie nawijania (osiągnięcie planu) NIE oznacza pozycji
    // jako zrealizowanej — czeka jeszcze na przewinięcie. Zielony przez "w pełni wyprodukowane"
    // dotyczy tylko linii małych szpul; duże stają się zielone dopiero po przewinięciu (wyżej).
    const selectedLineObj = productionLines.find((l) => l.id === selectedLineId);
    const isBigSpoolLine = selectedLineObj?.spool_type === 'big';
    if (!isBigSpoolLine && doProdukcji > 0 && wyprodukowano >= doProdukcji) return '#d4edda';

    // Pozycja zamknięta (zakończona) zawsze na zielono
    switch (status) {
      case 'pending':
        return 'white';
      case 'in_progress':
        return '#fff3cd'; // żółty
      case 'completed':
        return '#d4edda'; // zielony
      case 'problem_bb':
        return 'white'; // brak barwnika – świeci się tylko komórka Barwnik, wiersz biały
      case 'problem_awaria':
        return '#f8d7da'; // czerwony
      case 'niepelna_produkcja':
        return '#fff3cd'; // żółty (pozostaje żółty)
      default:
        return 'white';
    }
  };

  const handleRowClick = async (item: ProductionItem & { assignmentId?: number }) => {
    if (!item.do_produkcji || String(item.do_produkcji).trim() === '') return;
    const assignmentId = (item as any).assignmentId;
    if (!assignmentId) return;
    const currentStatus = getItemStatus(item);

    // For pending items, show modal with question about production — first, on every start, the
    // question whether production of each item already produced in full on this line is finished.
    if (currentStatus === 'pending') {
      setPendingItem({ item, assignmentId });
      const unfinished = unfinishedItemsOnLine(item.id);
      if (unfinished.length > 0) {
        setUnfinishedItems(unfinished);
      } else {
        setShowStartProductionModal(true);
      }
      return;
    }

    // For in_progress items, show completion modal
    if (currentStatus === 'in_progress') {
      setActiveItem({ item, assignmentId });
      setShowCompleteProductionModal(true);
      return;
    }

    // For completed items, allow reset to pending (only admin/kierownik_produkcji)
    if (currentStatus === 'completed') {
      // Zrealizowaną pozycję z DOWOLNEJ linii można oznaczyć „do przewinięcia" (niebieski)
      // — trafia wtedy na Stację przewijania. Dostępne dla każdego operatora, dopóki
      // pozycja nie ma jeszcze wpisu przewijania.
      // Pozycja wysłana na stację automatycznie (po przekroczeniu progu wagi) ma już wpis,
      // ale dopóki nikt jej nie oznaczył, wiersz nie jest niebieski — dalej pozwalamy oznaczyć.
      if (canMarkToRewind(item.rewind_entries)) {
        setRewindPrompt({ item, assignmentId });
        return;
      }
      if (user?.role === 'admin' || user?.role === 'kierownik_produkcji') {
        setItemToCancel({ item, assignmentId });
        setShowCancelProductionModal(true);
      }
      return;
    }

    // For problem statuses, allow reset to pending (only admin/kierownik_produkcji)
    if (currentStatus === 'problem_bb' || currentStatus === 'problem_awaria' || currentStatus === 'niepelna_produkcja') {
      if (user?.role === 'admin' || user?.role === 'kierownik_produkcji') {
        setItemToCancel({ item, assignmentId });
        setShowCancelProductionModal(true);
      }
      return;
    }
  };

  /**
   * Items of the selected line only (every week) that are not closed although the produced quantity
   * reached "Do produkcji", except `exceptItemId`.
   */
  const unfinishedItemsOnLine = (exceptItemId: number): Array<{ item: ProductionItem; assignmentId: number }> => {
    const unfinished = new Map<number, { item: ProductionItem; assignmentId: number }>();
    assignments
      .filter((a) => a.production_line_id === selectedLineId)
      .forEach((a) =>
        a.items.forEach((i) => {
          const planned = parseInt(i.do_produkcji || '0', 10) || 0;
          const produced = parseQty(i.zmiana_1) + parseQty(i.zmiana_2);
          if (i.id !== exceptItemId && UNFINISHED_STATUSES.includes(getItemStatus(i)) && planned > 0 && produced >= planned) {
            unfinished.set(i.id, { item: i, assignmentId: a.id });
          }
        }),
      );
    return Array.from(unfinished.values());
  };

  /** Next question, or — after the last one — the usual start question of the new item. */
  const nextUnfinishedQuestion = () => {
    const rest = unfinishedItems.slice(1);
    setUnfinishedItems(rest);
    if (rest.length === 0) setShowStartProductionModal(true);
  };

  // "Tak" closes the item (it turns blue at the rewinding station); "Nie" leaves it in progress.
  const handleUnfinishedFinished = async () => {
    const current = unfinishedItems[0];
    if (!current) return;
    if (!selectedShift) {
      alert(t('shift.mustSelectShift'));
      return;
    }
    setClosingUnfinished(true);
    try {
      await api.put(`/production-assignments/${current.assignmentId}/items/${current.item.id}/status`, {
        status: 'completed',
        shift: selectedShift,
      });
      fetchAssignments();
      nextUnfinishedQuestion();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotCompleteProduction')}`);
    } finally {
      setClosingUnfinished(false);
    }
  };

  const handleUnfinishedReminderCancel = () => {
    setUnfinishedItems([]);
    setPendingItem(null);
  };

  const handleStartProductionConfirm = async () => {
    if (!pendingItem) return;
    
    try {
      await api.put(`/production-assignments/${pendingItem.assignmentId}/items/${pendingItem.item.id}/status`, {
        status: 'in_progress',
      });
      setShowStartProductionModal(false);
      setPendingItem(null);
      setActiveDowntime(null);
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotStartProduction')}`);
    }
  };

  const handleStartProductionCancel = () => {
    setShowStartProductionModal(false);
    setPendingItem(null);
  };

  const handleCompleteProductionConfirm = async () => {
    if (!activeItem) return;
    if (!selectedShift) {
      alert(t('shift.mustSelectShift'));
      return;
    }
    
    try {
      const res = await api.put(`/production-assignments/${activeItem.assignmentId}/items/${activeItem.item.id}/status`, {
        status: 'completed',
        shift: selectedShift,
      });
      setShowCompleteProductionModal(false);
      setActiveItem(null);
      fetchAssignments();
      if (res.data?.needs_qr && res.data?.item) {
        setQrItem(res.data.item);
      }
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotCompleteProduction')}`);
    }
  };

  const handleCompleteProductionCancel = () => {
    setShowCompleteProductionModal(false);
    setShowProblemOptionsModal(true);
  };

  const handleCompleteOnLimitConfirm = async () => {
    if (!itemReachedLimit || !selectedShift) {
      alert(t('shift.mustSelectShift'));
      return;
    }
    
    try {
      const res = await api.put(`/production-assignments/${itemReachedLimit.assignmentId}/items/${itemReachedLimit.item.id}/status`, {
        status: 'completed',
        shift: selectedShift,
      });
      setShowCompleteOnLimitModal(false);
      setItemReachedLimit(null);
      fetchAssignments();
      if (res.data?.needs_qr && res.data?.item) {
        setQrItem(res.data.item);
      }
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotCompleteProduction')}`);
    }
  };

  const handleCompleteOnLimitCancel = () => {
    setShowCompleteOnLimitModal(false);
    setItemReachedLimit(null);
  };

  const handleProblemOption = async (option: 'continue' | 'incomplete' | 'problem_bb' | 'problem_awaria') => {
    if (!activeItem) return;

    if (option === 'continue') {
      // Produkuję dalej - just close modals, keep status in_progress
      setShowProblemOptionsModal(false);
      setActiveItem(null);
      return;
    }

    if (option === 'incomplete') {
      // Niepełna produkcja - show quantity input modal
      setShowProblemOptionsModal(false);
      setShowIncompleteProductionModal(true);
      return;
    }

    // Problem BB or Problem Awaria
    try {
      await api.put(`/production-assignments/${activeItem.assignmentId}/items/${activeItem.item.id}/status`, {
        status: option,
      });
      setShowProblemOptionsModal(false);
      setActiveItem(null);
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotUpdateStatus')}`);
    }
  };

  const handleIncompleteProductionSave = async () => {
    if (!activeItem || !incompleteQuantity || parseInt(incompleteQuantity) < 0) {
      alert(t('shift.mustProvideCorrectQuantity'));
      return;
    }
    if (!selectedShift) {
      alert(t('shift.mustSelectShift'));
      return;
    }

    try {
      await api.put(`/production-assignments/${activeItem.assignmentId}/items/${activeItem.item.id}/status`, {
        status: 'niepelna_produkcja',
        wykonano_ilosc: parseInt(incompleteQuantity),
        shift: selectedShift,
      });
      setShowIncompleteProductionModal(false);
      setActiveItem(null);
      setIncompleteQuantity('');
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotSaveIncomplete')}`);
    }
  };

  const handleIncompleteProductionCancel = () => {
    setShowIncompleteProductionModal(false);
    setIncompleteQuantity('');
    setShowProblemOptionsModal(true);
  };

  const handleCancelProductionConfirm = async () => {
    if (!itemToCancel) return;
    
    try {
      await api.put(`/production-assignments/${itemToCancel.assignmentId}/items/${itemToCancel.item.id}/status`, {
        status: 'pending',
        cancel_production: true, // Flag to indicate cancellation
      });
      setShowCancelProductionModal(false);
      setItemToCancel(null);
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotCancelProduction')}`);
    }
  };

  const handleRemoveProblemConfirm = async () => {
    if (!itemToCancel) return;
    
    try {
      await api.put(`/production-assignments/${itemToCancel.assignmentId}/items/${itemToCancel.item.id}/status`, {
        status: 'in_progress',
      });
      setShowCancelProductionModal(false);
      setItemToCancel(null);
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.alertCouldNotRemoveProblem')}`);
    }
  };

  const handleCancelProductionCancel = () => {
    setShowCancelProductionModal(false);
    setItemToCancel(null);
  };

  const handleNotesClick = async (e: React.MouseEvent, item: ProductionItem & { assignmentId?: number }) => {
    e.stopPropagation();
    if (!item.do_produkcji || String(item.do_produkcji).trim() === '') return;
    const assignmentId = (item as any).assignmentId;
    if (!assignmentId) return;
    
    setEditingNotes({ itemId: item.id, assignmentId });
    
    // Find current problem type if notes match a problem type name
    const currentNotes = item.pivot?.notes || '';
    const matchingProblemType = problemTypes.find(pt => pt.name === currentNotes);
    setSelectedProblemTypeId(matchingProblemType?.id || null);
    setNewNotatka('');

    setShowNotesModal(true);
    await Promise.all([
      fetchNotesHistory(assignmentId, item.id),
      fetchNotatki(assignmentId, item.id),
    ]);
  };

  const fetchNotatki = async (assignmentId: number, itemId: number) => {
    try {
      const res = await api.get(`/production-assignments/${assignmentId}/items/${itemId}/notatki`);
      setNotatkiList(res.data || []);
    } catch {
      setNotatkiList([]);
    }
  };

  const handleAddNotatka = async () => {
    if (!editingNotes || !newNotatka.trim()) return;
    setSavingNotatka(true);
    try {
      await api.post(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notatki`, {
        tresc: newNotatka.trim(),
      });
      setNewNotatka('');
      await fetchNotatki(editingNotes.assignmentId, editingNotes.itemId);
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('common.errorOccurred')}`);
    } finally {
      setSavingNotatka(false);
    }
  };

  const handleDeleteNotatka = async (notatkaId: number) => {
    if (!editingNotes) return;
    try {
      await api.delete(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notatki/${notatkaId}`);
      await fetchNotatki(editingNotes.assignmentId, editingNotes.itemId);
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('common.errorOccurred')}`);
    }
  };

  const handleSaveNotes = async () => {
    if (!editingNotes || !selectedProblemTypeId) {
      alert(t('myProduction.selectProblemFromList'));
      return;
    }
    
    try {
      await api.put(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notes`, {
        problem_type_id: selectedProblemTypeId,
      });
      setShowNotesModal(false);
      setEditingNotes(null);
      setSelectedProblemTypeId(null);
      setNotesHistory([]);
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.couldNotSaveNote')}`);
    }
  };

  const handleDeleteNotes = async () => {
    if (!editingNotes) return;
    if (!window.confirm(t('myProduction.confirmDeleteNote'))) return;
    try {
      await api.put(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notes`, {
        problem_type_id: null,
        note: null,
      });
      setShowNotesModal(false);
      setEditingNotes(null);
      setSelectedProblemTypeId(null);
      setNotesHistory([]);
      fetchAssignments();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.couldNotDeleteNote')}`);
    }
  };

  const fetchQuantityHistory = async (itemId: number) => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/production-items/${itemId}/history`);
      setQuantityHistory(response.data || []);
    } catch (error: any) {
      console.error('Error fetching quantity history:', error);
      setQuantityHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleQuantityClick = async (e: React.MouseEvent, item: ProductionItem, field: 'zmiana_1' | 'zmiana_2') => {
    e.stopPropagation();
    if (!item.do_produkcji || String(item.do_produkcji).trim() === '') return;
    // Po przewinięciu ilość jest rozliczona na Stacji przewijania — nie wolno jej już zmieniać.
    if (isQuantityLockedByRewind(item.rewind_entries)) {
      alert(t('myProduction.alertQuantityLockedByRewind'));
      return;
    }

    // Big-spool line: the spool is weighed and its label printed — the whole pieces of the weight go to
    // the clicked shift. Items whose quantity was already typed finish with the old quantity entry.
    const assignmentId = (item as any).assignmentId;
    const typedQuantity = parseQty(item.zmiana_1) + parseQty(item.zmiana_2);
    if (isBigSpoolLine && assignmentId && (item.weighed_spools || typedQuantity === 0)) {
      if (getItemStatus(item) === 'completed') {
        alert(t('spoolLabel.finishedInfo'));
        return;
      }
      setSpoolTarget({ itemId: item.id, assignmentId, field });
      return;
    }

    setEditingQuantity({ itemId: item.id, field });
    setQuantityValue('');
    setShowQuantityModal(true);
    await fetchQuantityHistory(item.id);
  };

  const handleRewindClick = async (item: ProductionItem & { assignmentId?: number }) => {
    const assignmentId = (item as any).assignmentId;
    if (!assignmentId) return;
    let warehouseItem: any = null;
    try {
      const response = await api.post('/warehouse/large/add', {
        production_item_id: item.id,
        production_assignment_id: assignmentId,
      });
      // Wpis magazynowy (nowy lub już istniejący) — do niego przypinamy ręcznie wpisaną wagę szpuli.
      warehouseItem = response.data?.item ?? null;
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.unknownError')}`);
      return;
    }
    // Odśwież, aby wiersz od razu podświetlił się na niebiesko (pozycja na liście przewijania).
    await fetchAssignments();
    // Pokaż QR do wydruku (etykieta na szpulę). Bez przekierowania do magazynu.
    setQrItem({
      ...item,
      warehouseItemId: warehouseItem?.id ?? null,
      actualWeight: warehouseItem?.actual_weight ?? null,
    });
  };

  const handleBarwnikClick = (_e: React.MouseEvent, item: ProductionItem & { assignmentId?: number }) => {
    if (!item.do_produkcji || String(item.do_produkcji).trim() === '') return;
    setBarwnikItem(item);
  };

  /**
   * "Nie" now means the dye is confirmed MISSING, not merely unconfirmed — the backend raises
   * the "Brak barwnika" problem on the open rows of the position, so the line turns red at once.
   * "Nie sprawdzono" clears the mark without touching the problem flow.
   */
  const handleBarwnikConfirm = async (status: 'available' | 'missing' | 'unknown') => {
    if (!barwnikItem) return;
    setSavingBarwnik(true);
    try {
      await api.put(`/production-items/${barwnikItem.id}/barwnik-dostarczony`, { status });
      await fetchAssignments();
      setBarwnikItem(null);
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.unknownError')}`);
    } finally {
      setSavingBarwnik(false);
    }
  };

  const handleSaveQuantity = async (onSaved?: () => void) => {
    if (!editingQuantity) return;

    // Sprawdź limit maksymalnej ilości
    const currentItem = currentItems.find(item => item.id === editingQuantity.itemId);
    if (!currentItem) return;

    const maxQuantity = currentItem.do_produkcji ? parseInt(currentItem.do_produkcji) : null;
    const enteredQuantity = parseInt(quantityValue);

    if (isNaN(enteredQuantity)) {
      alert(t('myProduction.alertEnterValidQuantity'));
      return;
    }

    // Negative value - show confirmation popup
    if (enteredQuantity < 0) {
      setShowNegativeConfirm(true);
      return;
    }

    // Nadprodukcja ponad plan jest dozwolona (mała linia → „Nadwyżka materiałowa",
    // duża → podgląd). Wcześniej pojedynczy wpis > plan był blokowany — teraz przechodzi.

    try {
      const quantityRes = await api.put(
        `/production-items/${editingQuantity.itemId}/quantity`,
        { [editingQuantity.field]: quantityValue, production_line_id: selectedLineId }
      );

      if (quantityRes.data?.needs_qr && quantityRes.data?.qr_item && !onSaved) {
        setQrItem(quantityRes.data.qr_item);
      }

      // Zapis ilości NIE kończy produkcji — status zostaje bez zmian, nawet po osiągnięciu limitu.
      await fetchAssignments();

      setShowQuantityModal(false);
      setEditingQuantity(null);
      setQuantityValue('');
      if (onSaved) { onSaved(); return; }

      alert(t('myProduction.alertQuantitySaved'));
      await fetchQuantityHistory(editingQuantity.itemId);
    } catch (error: any) {
      console.error('Error saving quantity:', error);
      const data = error.response?.data;
      const errorMessage = data?.message || t('myProduction.unknownError');
      const quantityAlert = getProductionQuantitySaveErrorAlert(error, t);
      if (quantityAlert) {
        alert(quantityAlert);
      } else {
        alert(t('myProduction.alertQuantitySaveError', { message: errorMessage }));
      }
    }
  };

  const handleNegativeConfirmSave = async () => {
    if (!editingQuantity) return;
    try {
      await api.put(
        `/production-items/${editingQuantity.itemId}/quantity`,
        { [editingQuantity.field]: quantityValue, production_line_id: selectedLineId }
      );
      await fetchAssignments();
      alert(t('myProduction.alertQuantitySaved'));
      setShowNegativeConfirm(false);
      setShowQuantityModal(false);
      setEditingQuantity(null);
      setQuantityValue('');
      await fetchQuantityHistory(editingQuantity.itemId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('myProduction.unknownError');
      alert(t('myProduction.alertQuantitySaveError', { message: errorMessage }));
    }
  };

  const handleNegativeHoldStart = () => {
    negativeHoldStartRef.current = Date.now();
    setNegativeHoldProgress(0);
    negativeHoldTimerRef.current = setInterval(() => {
      if (negativeHoldStartRef.current) {
        const elapsed = Date.now() - negativeHoldStartRef.current;
        const progress = Math.min((elapsed / 3000) * 100, 100);
        setNegativeHoldProgress(progress);
        if (elapsed >= 3000) {
          if (negativeHoldTimerRef.current) {
            clearInterval(negativeHoldTimerRef.current);
            negativeHoldTimerRef.current = null;
          }
          negativeHoldStartRef.current = null;
          setNegativeHoldProgress(0);
          handleNegativeConfirmSave();
        }
      }
    }, 50);
  };

  const handleNegativeHoldEnd = () => {
    if (negativeHoldTimerRef.current) {
      clearInterval(negativeHoldTimerRef.current);
      negativeHoldTimerRef.current = null;
    }
    negativeHoldStartRef.current = null;
    setNegativeHoldProgress(0);
  };

  const getActiveAssignment = (lineId: number) => {
    return assignments.find(
      (a) => a.production_line_id === lineId && (a.status === 'in_progress' || a.status === 'pending')
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <h1>{t('myProduction.title')}</h1>
        <AutoRefreshCountdown intervalSeconds={600} onRefresh={() => fetchAssignments(true)} />
      </div>

      {!selectedLineId && productionLines.length > 0 && (
        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
          <p>{t('myProduction.selectWorkstationPrompt')}</p>
          <div style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: '500', color: '#4a5568', fontSize: '16px', marginRight: '10px' }}>
              {t('auth.selectLine')}:
            </label>
            <select
              value={selectedLineId || ''}
              onChange={(e) => setSelectedLineId(e.target.value ? parseInt(e.target.value) : null)}
              style={{
                padding: '10px 15px',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                cursor: 'pointer',
                minWidth: '200px',
              }}
            >
              <option value="">{`-- ${t('admin.selectLine')} --`}</option>
              {productionLines.map((line) => (
                <option key={line.id} value={line.id}>
                  {line.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedLineId && (
        <div>
          <h2>
            {t('myProduction.assignedTasks', { lineName: productionLines.find((l) => l.id === selectedLineId)?.name || '' })}
          </h2>
          
          {/* Search filter */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('adminSearch.searchPlaceholder')}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '10px 14px',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#4a5568',
                outline: 'none',
              }}
            />
          </div>

          {/* Week Selector - guziki zamiast listy rozwijanej */}
          {availableWeeks.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '500', color: '#4a5568', fontSize: '16px', marginBottom: '10px' }}>
                {t('myProduction.selectWeek')}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => selectWeek(null)}
                  style={{
                    padding: '12px 18px',
                    border: selectedWeek === null ? '3px solid #667eea' : '2px solid #e1e8ed',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: selectedWeek === null ? 600 : 400,
                    backgroundColor: selectedWeek === null ? '#e7f3ff' : 'white',
                    color: selectedWeek === null ? '#667eea' : '#4a5568',
                    cursor: 'pointer',
                  }}
                >
                  {t('myProduction.allWeeks')}
                </button>
                {availableWeeks.map(({ week, is_closed, is_empty }) => {
                  const selected = selectedWeek === week;
                  const loading = loadingWeek === week;
                  return (
                    <button
                      key={week}
                      type="button"
                      onClick={() => selectWeek(week)}
                      title={is_closed ? 'Tydzień ukończony w 100%' : is_empty ? 'Tydzień pusty — pozycje przeniesione' : undefined}
                      style={{
                        padding: '12px 18px',
                        border: selected
                          ? '3px solid #667eea'
                          : is_closed ? '2px solid #38a169' : '2px solid #e1e8ed',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: selected ? 600 : 400,
                        backgroundColor: selected
                          ? '#e7f3ff'
                          : is_closed ? '#f0fff4' : is_empty ? '#f7fafc' : 'white',
                        color: selected
                          ? '#667eea'
                          : is_closed ? '#2f855a' : is_empty ? '#a0aec0' : '#4a5568',
                        cursor: 'pointer',
                        textDecoration: is_empty ? 'line-through' : 'none',
                        opacity: loading ? 0.6 : (is_empty ? 0.7 : 1),
                      }}
                    >
                      {week}{loading ? ' …' : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Realizacja listy w kilogramach: wszystkie zaplanowane kg wybranej linii (i tygodnia,
              jeśli wybrany) to 100%, a pasek pokazuje ile z nich już wyprodukowano. */}
          {completion.plannedKg > 0 && (
            <div style={{
              marginBottom: '20px', padding: '14px 16px', backgroundColor: 'white',
              border: '2px solid #e1e8ed', borderRadius: '8px',
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: '12px', flexWrap: 'wrap', marginBottom: '8px',
              }}>
                <span style={{ fontWeight: 600, color: '#4a5568', fontSize: '15px' }}>
                  {t('myProduction.completionTitle')}
                  {selectedWeek ? ` — ${selectedWeek}` : ` — ${t('myProduction.allWeeks')}`}
                </span>
                <span style={{ fontSize: '20px', fontWeight: 700, color: '#667eea' }}>
                  {completion.percent.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}%
                </span>
              </div>
              <div style={{ height: '14px', backgroundColor: '#edf2f7', borderRadius: '7px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(completion.percent, 100)}%`, height: '100%',
                  backgroundColor: '#667eea', transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ marginTop: '6px', fontSize: '13px', color: '#718096' }}>
                {t('myProduction.completionKg', {
                  produced: formatKg(completion.producedKg),
                  planned: formatKg(completion.plannedKg),
                })}
              </div>
            </div>
          )}

          {currentItems.length > 0 ? (
            <>
              {/* Downtime buttons */}
              <div key={language} style={{ marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {!activeDowntime ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleStartDowntime('czyszczenie')}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: '#ffc107',
                        color: '#856404',
                        border: '2px solid #e0a800',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '15px',
                      }}
                    >
                      {t('downtime.cleaning')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStartDowntime('awaria')}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: '2px solid #c82333',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '15px',
                      }}
                    >
                      {t('downtime.breakdown')}
                    </button>
                  </>
                ) : (
                  <div
                    className="downtime-pulse"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      backgroundColor: activeDowntime.type === 'czyszczenie' ? '#fff3cd' : '#f8d7da',
                      border: `2px solid ${activeDowntime.type === 'czyszczenie' ? '#ffc107' : '#dc3545'}`,
                    }}
                  >
                    <span style={{ fontWeight: '600', fontSize: '15px', color: activeDowntime.type === 'czyszczenie' ? '#856404' : '#721c24' }}>
                      {activeDowntime.type === 'czyszczenie' ? t('downtime.cleaning') : t('downtime.breakdown')}
                    </span>
                    <span style={{ fontWeight: '700', fontSize: '20px', fontFamily: 'monospace', color: activeDowntime.type === 'czyszczenie' ? '#856404' : '#721c24' }}>
                      {downtimeElapsed}
                    </span>
                    <button
                      type="button"
                      onClick={handleStopDowntime}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                      }}
                    >
                      {t('downtime.end')}
                    </button>
                  </div>
                )}
              </div>

              <p style={{ marginBottom: '10px', fontSize: '10px', color: '#666' }}>
                {t('myProduction.clickRowToChangeStatus')}
              </p>
              {(() => {
                const itemsWithNotatki = currentItems.filter(
                  (it: any) => Array.isArray(it.notatki) && it.notatki.length > 0
                );
                if (itemsWithNotatki.length === 0) return null;
                const visible = itemsWithNotatki.slice(0, 6);
                const remaining = itemsWithNotatki.length - visible.length;
                const label = (it: any) =>
                  it.indeks_kat || it.nazwa_oryg || it.material || t('myProduction.notesAlertUnnamed');
                const notatkaText = (it: any) => String(it.notatki?.[0]?.tresc || '').trim().replace(/\s+/g, ' ');
                return (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '12px 16px',
                      backgroundColor: '#e7f3ff',
                      border: '2px solid #667eea',
                      borderRadius: 8,
                      boxShadow: '0 2px 4px rgba(102, 126, 234, 0.15)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 20 }}>📝</span>
                      <strong style={{ color: '#3b4cca', fontSize: 14 }}>
                        {t('myProduction.notatkiInfoTitle', { count: itemsWithNotatki.length })}
                      </strong>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 28, color: '#2d3748', fontSize: 13, lineHeight: 1.6 }}>
                      {visible.map((it: any) => {
                        const note = notatkaText(it);
                        const excerpt = note.length > 80 ? note.slice(0, 80) + '…' : note;
                        const extra = (it.notatki?.length || 0) - 1;
                        return (
                          <li key={it.id}>
                            <strong>{label(it)}</strong>
                            {excerpt && <span style={{ color: '#4a5568' }}> - {excerpt}</span>}
                            {extra > 0 && <span style={{ color: '#718096' }}> (+{extra})</span>}
                          </li>
                        );
                      })}
                      {remaining > 0 && (
                        <li style={{ fontStyle: 'italic' }}>
                          {t('myProduction.notesAlertMore', { count: remaining })}
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })()}
              <ProductionTable
                items={currentItems}
                onRowClick={handleRowClick}
                onNotesClick={handleNotesClick}
                onQuantityClick={handleQuantityClick}
                onBarwnikClick={handleBarwnikClick}
                getRowColor={getRowColor}
                getItemStatus={getItemStatus}
                showTotalWeightColumn
              />
              {/* Legenda kolorów — 1:1 z getRowColor() powyżej. Zmieniając kolory wiersza,
                  zaktualizuj też tę listę. */}
              <div style={{ marginTop: '12px', color: '#4a5568', fontSize: '11px' }}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                  {t('myProduction.legendTitle')} — {t('myProduction.itemCount')}: {currentItems.length}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
                  {[
                    { color: 'white', label: t('myProduction.legendPending') },
                    { color: '#fff3cd', label: t('myProduction.legendInProgress') },
                    { color: '#cfe2ff', label: t('myProduction.legendToRewind') },
                    { color: '#d4edda', label: t('myProduction.legendCompleted') },
                    { color: '#f8d7da', label: t('myProduction.legendProblem') },
                    { color: '#c3e6cb', label: t('myProduction.legendDyeDelivered') },
                    { color: '#fde047', label: t('myProduction.legendNoteCell') },
                  ].map(({ color, label }) => (
                    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: color,
                          border: '1px solid #cbd5e1',
                          borderRadius: 3,
                          flexShrink: 0,
                        }}
                      />
                      {label}
                    </span>
                  ))}
                </div>
                <div style={{ marginTop: '6px', color: '#718096' }}>
                  {t('myProduction.legendBlinkHint')}
                </div>
              </div>
            </>
          ) : (
            <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
              <p>{t('myProduction.noTasksAssigned')}{selectedWeek ? ` ${t('myProduction.inWeek')} ${selectedWeek}` : ''}.</p>
            </div>
          )}
        </div>
      )}

      {!selectedLineId && productionLines.length > 0 && (
        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
          <p>{t('myProduction.selectWorkstationPrompt')}</p>
        </div>
      )}

      {showNotesModal && editingNotes && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#667eea' }}>{t('table.notes')}</h3>

            {/* Weighed big spools: printed labels — their own worker corrects a wrong weight here. */}
            {(() => {
              const currentItem = currentItems.find(item => item.id === editingNotes.itemId);
              if (!currentItem?.weighed_spools) return null;
              return (
                <PrintedSpoolsList
                  itemId={currentItem.id}
                  minWeightKg={parseWeightKg(currentItem.waga)}
                  lineId={selectedLine?.id ?? null}
                  onChanged={() => { fetchAssignments(true); }}
                />
              );
            })()}

            {/* Aktualna uwaga */}
            {(() => {
              const currentItem = currentItems.find(item => item.id === editingNotes.itemId);
              const currentNote = currentItem?.pivot?.notes || currentItem?.uwagi;
              if (currentNote) {
                return (
                  <div style={{
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #f59e0b'
                  }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '14px' }}>
                      ⚠️ {t('myProduction.currentNoteLabel')}
                    </label>
                    <div style={{ color: '#78350f', fontSize: '15px', fontWeight: '500' }}>
                      {translateProblemTypeName(currentNote, t)}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', color: '#4a5568' }}>
                {t('myProduction.selectProblem')}:
              </label>
              {problemTypes.length === 0 ? (
                <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '8px', fontSize: '10px', color: '#856404' }}>
                  ⚠️ {t('myProduction.noProblemTypesAvailable')}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    maxHeight: problemTypes.length > 4 ? '240px' : undefined,
                    overflowY: problemTypes.length > 4 ? 'auto' : undefined,
                    padding: problemTypes.length > 4 ? '4px 0' : 0,
                  }}
                >
                  {problemTypes.map((problemType) => {
                    const isActive = problemType.is_active !== false;
                    return (
                      <button
                        key={problemType.id}
                        type="button"
                        onClick={() => setSelectedProblemTypeId(selectedProblemTypeId === problemType.id ? null : problemType.id)}
                        style={{
                          padding: '14px 18px',
                          border: selectedProblemTypeId === problemType.id ? '3px solid #667eea' : '2px solid #e1e8ed',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: selectedProblemTypeId === problemType.id ? 600 : 400,
                          backgroundColor: selectedProblemTypeId === problemType.id ? '#e7f3ff' : (isActive ? 'white' : '#f9fafb'),
                          color: selectedProblemTypeId === problemType.id ? '#667eea' : (isActive ? '#4a5568' : '#9ca3af'),
                          cursor: 'pointer',
                          textAlign: 'left',
                          minWidth: '140px',
                          flex: '1 1 140px',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <span style={{ display: 'block', fontWeight: 'inherit' }}>
                          {translateProblemTypeName(problemType.name, t)}
                          {!isActive && (
                            <span style={{ marginLeft: '6px', fontSize: '12px', color: '#9ca3af', fontWeight: 400 }}>{t('myProduction.inactiveProblemType')}</span>
                          )}
                        </span>
                        {problemType.description && (
                          <span style={{ display: 'block', fontSize: '13px', color: isActive ? '#6b7280' : '#9ca3af', marginTop: '4px', fontWeight: 400 }}>
                            {translateProblemTypeDescription(problemType.description, t)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Notatki (ciche - bez alertu, wiele wpisow) */}
            <div style={{ marginTop: '24px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568', fontSize: '15px' }}>
                📝 {t('myProduction.notatkaLabel')}
              </label>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px' }}>
                {t('myProduction.notatkaHint')}
              </div>

              {notatkiList.length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {notatkiList.map((n) => (
                    <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#1e293b', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{n.tresc}</div>
                        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                          {n.user?.name || '—'} · {new Date(n.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteNotatka(n.id)}
                        title={t('common.delete')}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc3545', fontSize: '16px', lineHeight: 1 }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <textarea
                value={newNotatka}
                onChange={(e) => setNewNotatka(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder={t('myProduction.notatkaPlaceholder')}
                style={{
                  width: '100%', padding: '12px', border: '2px solid #e1e8ed', borderRadius: '8px',
                  fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleAddNotatka}
                  disabled={savingNotatka || !newNotatka.trim()}
                  style={{
                    padding: '8px 18px', backgroundColor: '#10b981', color: 'white', border: 'none',
                    borderRadius: '8px', cursor: (savingNotatka || !newNotatka.trim()) ? 'not-allowed' : 'pointer', fontWeight: 600,
                    opacity: (savingNotatka || !newNotatka.trim()) ? 0.6 : 1,
                  }}
                >
                  + {t('myProduction.notatkaAdd')}
                </button>
              </div>
            </div>

            {/* History Section */}
            <div style={{ marginTop: '30px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#4a5568', fontSize: '16px', fontWeight: '600' }}>
                📋 {t('myProduction.notesHistoryTitle')}
              </h4>
              {loadingNotesHistory ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#667eea' }}>
                  {t('myProduction.loadingHistory')}
                </div>
              ) : (
                <>
                  {notesHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '10px' }}>
                      {t('myProduction.noNotesHistory')}
                    </div>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e1e8ed' }}>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              {t('myProduction.historyDate')}
                            </th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              {t('myProduction.historyProblem')}
                            </th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              {t('myProduction.historyOperator')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {notesHistory.map((entry, index) => (
                            <tr
                              key={entry.id || index}
                              style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                              }}
                            >
                              <td style={{ padding: '10px', color: '#4a5568' }}>
                                {entry.created_at
                                  ? new Date(entry.created_at).toLocaleString(dateLocale, {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '-'}
                              </td>
                              <td style={{ padding: '10px', color: '#667eea', fontWeight: '600' }}>
                                {entry.problem_type?.name
                                  ? translateProblemTypeName(entry.problem_type.name, t)
                                  : entry.note
                                    ? translateProblemTypeName(entry.note, t)
                                    : '-'}
                              </td>
                              <td style={{ padding: '10px', color: '#4a5568' }}>
                                {entry.user?.name || t('myProduction.unknownUser')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleDeleteNotes}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                {t('myProduction.deleteNote')}
              </button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowNotesModal(false);
                    setEditingNotes(null);
                    setSelectedProblemTypeId(null);
                    setNotesHistory([]);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveNotes}
                  disabled={!selectedProblemTypeId}
                  style={{
                    padding: '10px 20px',
                    background: selectedProblemTypeId ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d3d3d3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                  cursor: selectedProblemTypeId ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  opacity: selectedProblemTypeId ? 1 : 0.6,
                }}
              >
                {t('common.save')}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Modal */}
      {showQuantityModal && editingQuantity && (() => {
        const currentItem = currentItems.find(item => item.id === editingQuantity.itemId);
        const maxQuantity = currentItem?.do_produkcji ? parseInt(currentItem.do_produkcji) : null;
        // Nadprodukcja dozwolona — nie blokujemy zapisu; przy przekroczeniu planu pokazujemy
        // tylko informację o nadwyżce (mała linia → materiałowa, duża → podgląd).
        const curZ1 = currentItem?.zmiana_1 ? parseInt(currentItem.zmiana_1) : 0;
        const curZ2 = currentItem?.zmiana_2 ? parseInt(currentItem.zmiana_2) : 0;
        const enteredNum = quantityValue !== '' && !isNaN(parseInt(quantityValue)) ? parseInt(quantityValue) : 0;
        const surplusPreview = maxQuantity !== null && enteredNum > 0 ? Math.max(0, (curZ1 + curZ2 + enteredNum) - maxQuantity) : 0;
        const isSaveDisabled = false;
        
        const quantityShiftName =
          editingQuantity.field === 'zmiana_1' ? t('production.shift1') : t('production.shift2');
        return (
        <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <h3 style={{ marginTop: 0, color: '#667eea' }}>
                {t('myProduction.quantityProducedTitle', { shift: quantityShiftName })}
              </h3>
              {maxQuantity !== null && (
                <div style={{
                  marginTop: '15px',
                  padding: '12px',
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #667eea',
                  borderRadius: '8px',
                  color: '#4a5568',
                  fontSize: '10px'
                }}>
                  📊 {t('myProduction.maxQuantityToProduce', { max: maxQuantity })}
                  {currentItem && (
                    <> | {t('myProduction.currentQuantityValue')}: <strong>{currentItem[editingQuantity.field] || '0'}</strong></>
                  )}
                </div>
              )}
              <div style={{ marginTop: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  {t('myProduction.enterQuantityLabel')}
                </label>
                <input
                  type="number"
                  value={quantityValue}
                  onChange={(e) => setQuantityValue(e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${quantityValue !== '' && parseInt(quantityValue) < 0 ? '#dc3545' : '#e1e8ed'}`,
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    cursor: 'text',
                    color: quantityValue !== '' && parseInt(quantityValue) < 0 ? '#dc3545' : 'inherit',
                  }}
                  autoFocus
                />
                {surplusPreview > 0 && (
                  <div style={{
                    marginTop: '8px',
                    color: '#2563eb',
                    fontSize: '12px'
                  }}>
                    ℹ️ {isBigSpoolLine
                      ? t('myProduction.surplusInfoBig', { surplus: surplusPreview })
                      : t('myProduction.surplusInfoSmall', { surplus: surplusPreview })}
                  </div>
                )}
              </div>
            
            {/* History Section */}
            <div style={{ marginTop: '30px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#4a5568', fontSize: '16px', fontWeight: '600' }}>
                📊 {t('myProduction.quantityChangeHistoryTitle', { shift: quantityShiftName })}
              </h4>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#667eea' }}>
                  {t('myProduction.loadingHistory')}
                </div>
              ) : (
                <>
                  {quantityHistory
                    .filter((entry) => entry.shift === editingQuantity.field)
                    .length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '10px' }}>
                      {t('myProduction.noHistoryShift')}
                    </div>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e1e8ed' }}>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              {t('myProduction.historyDate')}
                            </th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              {t('myProduction.historyQuantityColumn')}
                            </th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              {t('myProduction.historyOperator')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {quantityHistory
                            .filter((entry) => entry.shift === editingQuantity.field)
                            .map((entry, index) => (
                              <tr
                                key={entry.id || index}
                                style={{
                                  borderBottom: '1px solid #f0f0f0',
                                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                }}
                              >
                                <td style={{ padding: '10px', color: '#4a5568' }}>
                                  {entry.produced_at
                                    ? new Date(entry.produced_at).toLocaleString('pl-PL', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '-'}
                                </td>
                                <td style={{ padding: '10px', color: parseInt(entry.quantity) < 0 ? '#dc3545' : '#667eea', fontWeight: '600' }}>
                                  {entry.quantity || '0'}
                                </td>
                                <td style={{ padding: '10px', color: '#4a5568' }}>
                                  {entry.user?.name || t('myProduction.unknownUser')}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Ręczne oznaczenie pozycji „Do przewijania" (wiersz robi się niebieski). Na liniach
                dużych szpul wpis tworzy też wydruk etykiety ważonej szpuli. */}
            <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Lewa strona: oznaczenie „Do przewijania" */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {/* Already waiting or already rewound — the position cannot be sent again. */}
              {currentItem && canMarkToRewind(currentItem.rewind_entries) && (
                <button
                  type="button"
                  disabled={isSaveDisabled}
                  onClick={() => {
                    if (!currentItem) return;
                    if (quantityValue.trim() !== '') {
                      // Najpierw zapisz wpisaną ilość, potem oznacz jako „do przewijania".
                      handleSaveQuantity(() => handleRewindClick(currentItem));
                    } else {
                      setShowQuantityModal(false);
                      setEditingQuantity(null);
                      setQuantityValue('');
                      handleRewindClick(currentItem);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'white',
                    color: isSaveDisabled ? '#9ca3af' : '#667eea',
                    border: `1px solid ${isSaveDisabled ? '#d3d3d3' : '#667eea'}`,
                    borderRadius: '6px',
                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    fontSize: '12px',
                    opacity: isSaveDisabled ? 0.6 : 1,
                  }}
                >
                  🔄 {t('myProduction.markAsToRewindConfirm')}
                </button>
              )}
              </div>

              {/* Prawa strona: anuluj + zapisz */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowQuantityModal(false);
                    setEditingQuantity(null);
                    setQuantityValue('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleSaveQuantity()}
                  disabled={isSaveDisabled}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: isSaveDisabled ? '#d3d3d3' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: isSaveDisabled ? 0.6 : 1,
                  }}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Negative Quantity Confirmation Modal */}
      {showNegativeConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>
              {t('myProduction.negativeConfirmTitle')}
            </h3>
            <p style={{ color: '#4a5568', fontSize: '14px' }}>
              {t('myProduction.negativeConfirmMessage', { value: quantityValue })}
            </p>
            <div style={{
              marginTop: '15px',
              padding: '12px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              color: '#721c24',
              fontSize: '12px',
            }}>
              {t('myProduction.negativeConfirmWarning')}
            </div>
            <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowNegativeConfirm(false);
                  handleNegativeHoldEnd();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <div style={{ position: 'relative' }}>
                <button
                  onMouseDown={handleNegativeHoldStart}
                  onMouseUp={handleNegativeHoldEnd}
                  onMouseLeave={handleNegativeHoldEnd}
                  onTouchStart={handleNegativeHoldStart}
                  onTouchEnd={handleNegativeHoldEnd}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    overflow: 'hidden',
                    position: 'relative',
                    userSelect: 'none',
                  }}
                >
                  {negativeHoldProgress > 0
                    ? t('nav.holdProgress', { progress: Math.round(negativeHoldProgress).toString() })
                    : t('myProduction.negativeConfirmHoldButton')}
                </button>
                {negativeHoldProgress > 0 && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '4px',
                    width: `${negativeHoldProgress}%`,
                    backgroundColor: '#fff',
                    borderRadius: '0 0 8px 8px',
                    transition: 'width 50ms linear',
                  }} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Before another item starts: is production of an item produced in full finished? */}
      {unfinishedItems.length > 0 && pendingItem && (() => {
        const current = unfinishedItems[0].item;
        const button = (background: string): React.CSSProperties => ({
          padding: '10px 20px',
          backgroundColor: background,
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: closingUnfinished ? 'wait' : 'pointer',
          fontWeight: '500',
          opacity: closingUnfinished ? 0.6 : 1,
        });
        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <h3 style={{ marginTop: 0, color: '#b45309', marginBottom: '16px' }}>
                {t('myProduction.unfinishedReminderTitle')}
              </h3>
              <p style={{ fontSize: '16px', marginBottom: '8px', color: '#4a5568' }}>
                {t('myProduction.unfinishedReminderQuestion')}
              </p>
              <p style={{ fontSize: '17px', margin: '0 0 6px', color: '#1a202c' }}>
                <strong>{current.nazwa_oryg || current.indeks_kat || t('myProduction.itemFallback')}</strong>
                {current.tydzien ? ` · ${t('myProduction.unfinishedReminderWeek', { week: current.tydzien })}` : ''}
              </p>
              <p style={{ fontSize: '14px', margin: '0 0 20px', color: '#64748b' }}>
                {t('myProduction.unfinishedReminderProduced', {
                  produced: parseQty(current.zmiana_1) + parseQty(current.zmiana_2),
                  planned: current.do_produkcji ?? '0',
                })}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button onClick={handleUnfinishedReminderCancel} disabled={closingUnfinished} style={button('#6c757d')}>
                  {t('common.cancel')}
                </button>
                <button onClick={nextUnfinishedQuestion} disabled={closingUnfinished} style={button('#f59e0b')}>
                  {t('myProduction.unfinishedReminderNo')}
                </button>
                <button onClick={handleUnfinishedFinished} disabled={closingUnfinished} style={button('#28a745')}>
                  {t('myProduction.unfinishedReminderYes')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Start Production Modal */}
      {showStartProductionModal && pendingItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#667eea', marginBottom: '20px' }}>
              {t('myProduction.startProductionTitle')}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#4a5568' }}>
              {t('myProduction.startProductionPrefix')}{' '}
              <strong>{pendingItem.item.nazwa_oryg || pendingItem.item.indeks_kat || t('myProduction.itemFallback')}</strong>
              {t('myProduction.startProductionSuffix')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleStartProductionCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleStartProductionConfirm}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                {t('common.yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Production Modal */}
      {showCompleteProductionModal && activeItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#28a745', marginBottom: '20px' }}>
              {t('myProduction.endProductionTitle')}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#4a5568' }}>
              {t('myProduction.endProductionPart1')}{' '}
              <strong>{activeItem.item.nazwa_oryg || activeItem.item.indeks_kat || t('myProduction.itemFallback')}</strong>{' '}
              {t('myProduction.endProductionPart2')}{' '}
              <strong>{activeItem.item.do_produkcji || '0'}</strong>
              {t('myProduction.endProductionSuffix')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCompleteProductionCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('common.no')}
              </button>
              <button
                onClick={handleCompleteProductionConfirm}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                {t('common.yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual "mark as to-rewind" prompt (big-spool lines), triggered by clicking the row.
          Reaching the planned kilograms sends the spool to the station on its own. */}
      {rewindPrompt && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white', padding: '30px', borderRadius: '12px',
            maxWidth: '480px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ marginTop: 0, color: '#667eea', marginBottom: '16px' }}>
              🔄 {t('myProduction.markAsToRewindTitle')}
            </h3>
            <p style={{ fontSize: '15px', marginBottom: '20px', color: '#4a5568' }}>
              <strong>{rewindPrompt.item.nazwa_oryg || rewindPrompt.item.indeks_kat || t('myProduction.itemFallback')}</strong>
              {' — '}
              {t('myProduction.markAsToRewindBody')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => setRewindPrompt(null)}
                style={{
                  padding: '10px 20px', backgroundColor: '#6c757d', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                {t('common.no')}
              </button>
              {/* Klik w zrealizowany wiersz zastępuje modal anulowania — kierownik/admin
                  musi mieć tu nadal dostęp do cofnięcia produkcji. */}
              {(user?.role === 'admin' || user?.role === 'kierownik_produkcji') && (
                <button
                  onClick={() => {
                    const p = rewindPrompt;
                    setRewindPrompt(null);
                    setItemToCancel({ item: p.item, assignmentId: p.assignmentId });
                    setShowCancelProductionModal(true);
                  }}
                  style={{
                    padding: '10px 20px', backgroundColor: '#dc3545', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500',
                  }}
                >
                  {t('myProduction.cancelModalTitleCancel')}
                </button>
              )}
              <button
                onClick={() => {
                  const p = rewindPrompt;
                  setRewindPrompt(null);
                  handleRewindClick({ ...p.item, assignmentId: p.assignmentId });
                }}
                style={{
                  padding: '10px 20px', backgroundColor: '#667eea', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500',
                }}
              >
                🔄 {t('myProduction.markAsToRewindConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Problem Options Modal */}
      {showProblemOptionsModal && activeItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#dc3545', marginBottom: '20px' }}>
              {t('myProduction.finishOptionsTitle')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => handleProblemOption('continue')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  border: '2px solid #ffc107',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  textAlign: 'left',
                }}
              >
                {t('myProduction.continueProducing')}
              </button>
              <button
                onClick={() => handleProblemOption('incomplete')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  border: '2px solid #ffc107',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  textAlign: 'left',
                }}
              >
                {t('production.status.incomplete')}
              </button>
              <button
                onClick={() => handleProblemOption('problem_bb')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  textAlign: 'left',
                }}
              >
                {t('production.status.problemBB')}
              </button>
              <button
                onClick={() => handleProblemOption('problem_awaria')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  textAlign: 'left',
                }}
              >
                {t('production.status.problemFailure')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Incomplete Production Modal */}
      {showIncompleteProductionModal && activeItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#ffc107', marginBottom: '20px' }}>
              {t('production.status.incomplete')}
            </h3>
            <p style={{ fontSize: '10px', marginBottom: '15px', color: '#4a5568' }}>
              {t('myProduction.enterHowMuchDone')}
            </p>
            <input
              type="number"
              value={incompleteQuantity}
              onChange={(e) => setIncompleteQuantity(e.target.value)}
              placeholder="0"
              min="0"
              max={activeItem.item.do_produkcji ? parseInt(activeItem.item.do_produkcji) : undefined}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleIncompleteProductionCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleIncompleteProductionSave}
                disabled={!incompleteQuantity || parseInt(incompleteQuantity) < 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ffc107',
                  color: '#856404',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: incompleteQuantity && parseInt(incompleteQuantity) >= 0 ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  opacity: incompleteQuantity && parseInt(incompleteQuantity) >= 0 ? 1 : 0.5,
                }}
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showContinueModal && otherUserActiveItem && (
        <ContinueActiveItemModal
          activeItem={otherUserActiveItem}
          onContinue={handleContinueOtherUserActiveItem}
          onCancel={handleCancelContinueOtherUser}
        />
      )}

      {showCloseModal && otherUserActiveItem && (
        <CloseActiveItemModal
          activeItem={otherUserActiveItem}
          onClose={handleCloseOtherUserActiveItem}
          onCancel={handleCancelCloseOtherUser}
        />
      )}

      {/* Complete on Limit Modal */}
      {showCompleteOnLimitModal && itemReachedLimit && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#667eea', marginBottom: '20px' }}>
              ✅ {t('myProduction.productionLimitReachedTitle')}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#4a5568' }}>
              {t('myProduction.productionLimitReachedMax', { max: itemReachedLimit.item.do_produkcji ?? '' })}
            </p>
            <p style={{ fontSize: '16px', marginBottom: '30px', color: '#4a5568' }}>
              {t('myProduction.finishWorkOnItemQuestion')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCompleteOnLimitCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('myProduction.noContinueWork')}
              </button>
              <button
                onClick={handleCompleteOnLimitConfirm}
                disabled={!selectedShift}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedShift ? 'pointer' : 'not-allowed',
                  opacity: selectedShift ? 1 : 0.6,
                  fontWeight: '500',
                }}
              >
                {t('myProduction.yesFinishWork')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Production Modal */}
      {showCancelProductionModal && itemToCancel && (() => {
        const cancelItemStatus = getItemStatus(itemToCancel.item);
        const isProblemStatus = cancelItemStatus === 'problem_bb' || cancelItemStatus === 'problem_awaria';
        return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: isProblemStatus ? '#0d6efd' : '#dc3545', marginBottom: '20px' }}>
              {isProblemStatus ? t('myProduction.cancelModalTitleProblem') : t('myProduction.cancelModalTitleCancel')}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '15px', color: '#4a5568' }}>
              {isProblemStatus
                ? t('myProduction.itemMarkedAsProblem')
                : t('myProduction.confirmCancelProductionPrefix')}
            </p>
            <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: '600', color: '#2d3748' }}>
              <strong>{itemToCancel.item.nazwa_oryg || itemToCancel.item.indeks_kat || t('common.notAvailable')}</strong>
            </p>
            {isProblemStatus ? (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#e7f3ff', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #0d6efd'
              }}>
                <p style={{ fontSize: '14px', margin: '5px 0', color: '#0a58ca' }}>
                  {t('myProduction.problemResolveHelp', {
                    inProgress: t('production.status.inProgress'),
                    pending: t('production.status.pending'),
                  })}
                </p>
              </div>
            ) : (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #ffc107'
            }}>
              <p style={{ fontSize: '10px', margin: '5px 0', color: '#856404' }}>
                {t('myProduction.cancelWarningIntro')}
              </p>
              <ul style={{ fontSize: '10px', margin: '10px 0', paddingLeft: '20px', color: '#856404' }}>
                <li>{t('myProduction.cancelBulletClearShifts')}</li>
                <li>{t('myProduction.cancelBulletKeepHistory')}</li>
                <li>{t('myProduction.cancelBulletResetPending', { pending: t('production.status.pending') })}</li>
                <li>{t('myProduction.cancelBulletLogged')}</li>
              </ul>
            </div>
            )}
            <p style={{ fontSize: isProblemStatus ? '14px' : '10px', marginBottom: '20px', color: isProblemStatus ? '#4a5568' : '#dc3545', fontWeight: isProblemStatus ? '400' : '600' }}>
              {isProblemStatus ? t('myProduction.chooseAction') : t('myProduction.confirmContinueCancel')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={handleCancelProductionCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('myProduction.closeModalNo')}
              </button>
              {isProblemStatus && (
                <button
                  onClick={handleRemoveProblemConfirm}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#0d6efd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  {t('myProduction.removeProblemButton', { status: t('production.status.inProgress') })}
                </button>
              )}
              <button
                onClick={handleCancelProductionConfirm}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                {isProblemStatus
                  ? t('myProduction.cancelProductionToPendingButton', { status: t('production.status.pending') })
                  : t('myProduction.yesCancelProduction')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {spoolTarget && (() => {
        const spoolItem = currentItems.find((i) => i.id === spoolTarget.itemId);
        if (!spoolItem) return null;
        return (
          <QRCodeModal
            item={spoolItem}
            spool={{
              assignmentId: spoolTarget.assignmentId,
              shift: spoolTarget.field,
              onPrinted: () => fetchAssignments(),
            }}
            onClose={() => setSpoolTarget(null)}
          />
        );
      })()}

      {SHOW_QR_MODAL && qrItem && (
        <QRCodeModal
          item={qrItem}
          warehouseItemId={qrItem.warehouseItemId}
          initialWeight={qrItem.actualWeight}
          onClose={() => setQrItem(null)}
        />
      )}

      {/* Barwnik delivered confirmation modal */}
      {barwnikItem && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '460px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#667eea', marginBottom: '16px' }}>
              🎨 {t('myProduction.barwnikModalTitle')}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '24px', color: '#4a5568' }}>
              {t('myProduction.barwnikModalQuestion')}
              {barwnikItem.barwnik ? (
                <> <strong>{barwnikItem.barwnik.replace(/\s+/g, ' ').trim()}</strong></>
              ) : null}
            </p>
            {barwnikItem.barwnik_status && (
              <p style={{ fontSize: '14px', marginTop: '-14px', marginBottom: '24px', color: '#718096' }}>
                {t('myProduction.barwnikCurrentState')}{' '}
                <strong style={{ color: barwnikItem.barwnik_status === 'missing' ? '#dc3545' : '#28a745' }}>
                  {barwnikItem.barwnik_status === 'missing'
                    ? t('myProduction.barwnikStateMissing')
                    : t('myProduction.barwnikStateAvailable')}
                </strong>
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setBarwnikItem(null)}
                disabled={savingBarwnik}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: savingBarwnik ? 'not-allowed' : 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              {barwnikItem.barwnik_status && (
                <button
                  type="button"
                  onClick={() => handleBarwnikConfirm('unknown')}
                  disabled={savingBarwnik}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#4a5568',
                    border: '2px solid #cbd5e1',
                    borderRadius: '8px',
                    cursor: savingBarwnik ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    opacity: savingBarwnik ? 0.6 : 1,
                  }}
                >
                  {t('myProduction.barwnikNotChecked')}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleBarwnikConfirm('missing')}
                disabled={savingBarwnik}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  color: '#dc3545',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  cursor: savingBarwnik ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  opacity: savingBarwnik ? 0.6 : 1,
                }}
              >
                {t('common.no')}
              </button>
              <button
                type="button"
                onClick={() => handleBarwnikConfirm('available')}
                disabled={savingBarwnik}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: savingBarwnik ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  opacity: savingBarwnik ? 0.6 : 1,
                }}
              >
                {t('common.yes')}
              </button>
            </div>
          </div>
        </div>
      )}
</div>
  );
};

export default MyProduction;
